import { useState, useRef, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import {
  Camera, Upload, Check, X,
  User, Shield, Zap, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';


const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes shimmer {
    0%   { background-position: -400px 0 }
    100% { background-position:  400px 0 }
  }

  .settings-main {
    --card-text: #ffffff;
    --card-text-rgb: 255 255 255;
    flex: 1; padding: 48px;
    animation: fadeUp 0.5s ease;
    width: 100%;
    color: var(--card-text);
    font-family: var(--org-font, 'DM Sans', sans-serif);
  }

  .page-title {
    font-size: 24px; font-weight: 800; margin-bottom: 4px;
    color: var(--brand-text, #fff);
    text-shadow: 0 0 18px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.5);
  }
  .page-subtitle {
    font-size: 14px; margin-bottom: 36px;
    color: rgb(var(--brand-text-rgb, 255 255 255) / 0.8);
  }

  .settings-grid {
    display: grid;
    grid-template-columns: 340px 1fr;
    gap: 20px;
    align-items: start;
  }

  /* ── Tarjetas: oscuras, borde neón de marca ── */
  .glass-card {
    background:
      linear-gradient(rgb(0 0 0 / 0.6), rgb(0 0 0 / 0.6)),
      rgb(var(--brand-bg-rgb, 6 5 20) / 0.9);
    backdrop-filter: blur(20px) saturate(160%);
    -webkit-backdrop-filter: blur(20px) saturate(160%);
    border: 1.5px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.7);
    box-shadow:
      0 0 20px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.35),
      inset 0 0 18px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.08);
    border-radius: 20px; overflow: hidden;
    animation: fadeUp 0.5s ease both;
  }
  .glass-card-header {
    padding: 18px 22px;
    border-bottom: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.35);
    display: flex; align-items: center; gap: 10px;
    color: var(--brand-secondary, #FF7300);
  }
  .glass-card-title { font-size: 14px; font-weight: 700; color: var(--card-text); }
  .glass-card-body  { padding: 22px; }

  /* ── Avatar ── */
  .avatar-center { display: flex; flex-direction: column; align-items: center; gap: 20px; }

  .avatar-ring {
    position: relative; width: 120px; height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg,
      var(--brand-primary, #FFA700), var(--brand-secondary, #FF7300),
      var(--brand-accent, #CCFF00), var(--brand-primary, #FFA700));
    background-size: 300% 300%;
    animation: shimmer 3s linear infinite;
    padding: 3px;
    cursor: pointer;
    border: none;
    display: block;
    box-shadow: 0 0 24px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.65);
  }
  .avatar-ring-inner {
    width: 100%; height: 100%; border-radius: 50%;
    overflow: hidden; background: rgb(var(--brand-bg-rgb, 6 5 20));
    position: relative;
  }
  .avatar-img {
    width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
    transition: opacity 0.2s;
  }
  .avatar-initials {
    width: 100%; height: 100%; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 40px; font-weight: 800; color: #fff;
  }
  .avatar-overlay {
    position: absolute; inset: 0; border-radius: 50%;
    background: rgba(0,0,0,0.5);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 4px;
    opacity: 0; transition: opacity 0.2s;
    color: #fff; font-size: 11px; font-weight: 600;
  }
  .avatar-ring:hover .avatar-overlay { opacity: 1; }

  .profile-name { font-size: 16px; font-weight: 800; color: var(--card-text); }
  .profile-role { font-size: 11px; margin-top: 2px; color: rgb(var(--card-text-rgb) / 0.75); }

  /* ── Zona de subida ── */
  .drop-zone {
    border: 2px dashed rgb(var(--brand-secondary-rgb, 255 115 0) / 0.6);
    border-radius: 14px; padding: 22px 16px;
    text-align: center; cursor: pointer;
    transition: all 0.2s; width: 100%;
    display: block;
    background: transparent;
    font: inherit;
    color: inherit;
  }
  .drop-zone:hover, .drop-zone.drag-over {
    border-color: var(--brand-secondary, #FF7300);
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.14);
    box-shadow: 0 0 18px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.4);
  }
  .drop-zone-icon {
    width: 40px; height: 40px; border-radius: 10px;
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.2);
    color: var(--brand-secondary, #FF7300);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 10px;
  }
  .drop-zone-text { font-size: 13px; font-weight: 600; color: var(--card-text); margin-bottom: 3px; }
  .drop-zone-sub  { font-size: 11px; color: rgb(var(--card-text-rgb) / 0.6); }

  .preview-wrap { position: relative; width: 100%; }
  .preview-img {
    width: 100%; border-radius: 12px; object-fit: cover;
    max-height: 180px; display: block;
  }
  .preview-actions { display: flex; gap: 8px; margin-top: 10px; }

  /* ── Botones ── */
  .btn {
    display: inline-flex; align-items: center; gap: 7px;
    border-radius: 10px; font-family: inherit;
    font-size: 13px; font-weight: 600; cursor: pointer;
    transition: all 0.18s; border: none; text-decoration: none;
  }
  .btn-indigo {
    background: linear-gradient(135deg, var(--brand-primary, #FFA700), var(--brand-secondary, #FF7300));
    color: #fff; padding: 11px 22px;
    text-shadow: 0 1px 2px rgba(0,0,0,0.35);
    box-shadow: 0 0 18px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.55);
  }
  .btn-indigo:hover {
    box-shadow: 0 0 30px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.9);
    transform: translateY(-1px);
  }
  .btn-indigo:disabled {
    background: rgb(var(--card-text-rgb) / 0.12);
    color: rgb(var(--card-text-rgb) / 0.4);
    box-shadow: none; cursor: not-allowed; transform: none; text-shadow: none;
  }
  .btn-ghost {
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.14);
    border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.5);
    color: var(--card-text); padding: 10px 18px;
  }
  .btn-ghost:hover {
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.28);
    box-shadow: 0 0 14px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.45);
  }
  .btn-sm { padding: 7px 14px; font-size: 12px; }

  .spinner {
    width: 16px; height: 16px; border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.25);
    border-top-color: #fff;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }

  /* ── Formulario ── */
  .field-group { display: flex; flex-direction: column; gap: 14px; }
  .field { display: flex; flex-direction: column; gap: 6px; }
  .field-label {
    font-size: 11px; font-weight: 700;
    letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--brand-secondary, #FF7300);
  }
  .field-input {
    background: rgb(0 0 0 / 0.35);
    border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.45);
    border-radius: 10px; color: var(--card-text);
    padding: 10px 14px; font-family: inherit;
    font-size: 13px; outline: none; width: 100%;
    transition: border 0.2s, box-shadow 0.2s;
  }
  .field-input:focus {
    border-color: var(--brand-secondary, #FF7300);
    box-shadow:
      0 0 0 3px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.25),
      0 0 14px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.45);
  }
  .field-input:disabled { opacity: 0.6; cursor: not-allowed; }
  .field-input::placeholder { color: rgb(var(--card-text-rgb) / 0.35); }

  /* ── Insignias XP / Nivel ── */
  .stat-badges { display: flex; gap: 10px; flex-wrap: wrap; }
  .stat-badge {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; border-radius: 12px;
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.14);
    border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.45);
    flex: 1; min-width: 90px;
  }
  .stat-badge-ico {
    width: 32px; height: 32px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.25);
    color: var(--brand-secondary, #FF7300);
  }
  .stat-badge-val { font-size: 18px; font-weight: 800; color: var(--card-text); line-height: 1; }
  .stat-badge-lbl { font-size: 10px; color: rgb(var(--card-text-rgb) / 0.65); margin-top: 2px; font-weight: 500; }

  /* ── Estilo de aprendizaje ── */
  .style-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .style-card {
    padding: 12px 14px; border-radius: 12px; cursor: pointer;
    border: 1.5px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.3);
    background: rgb(0 0 0 / 0.25);
    transition: all 0.18s; display: flex; align-items: center; gap: 10px;
    width: 100%;
    text-align: left;
    font: inherit;
    color: inherit;
  }
  .style-card:hover {
    border-color: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.75);
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.14);
  }
  .style-card.active {
    border-color: var(--brand-secondary, #FF7300);
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.24);
    box-shadow: 0 0 18px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.55);
  }
  .style-card-icon {
    width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 18px;
  }
  .style-card-name  { font-size: 12px; font-weight: 700; color: var(--card-text); }
  .style-card-desc  { font-size: 10px; color: rgb(var(--card-text-rgb) / 0.7); margin-top: 1px; }
  .style-check {
    margin-left: auto; width: 18px; height: 18px; border-radius: 50%;
    background: var(--brand-secondary, #FF7300);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  /* ── Tarjeta Cuenta ── */
  .account-list { display: flex; flex-direction: column; gap: 10px; }
  .account-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px; border-radius: 10px;
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.1);
    border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.35);
  }
  .account-row-label { font-size: 12px; font-weight: 500; color: rgb(var(--card-text-rgb) / 0.7); }
  .account-row-value { font-size: 12px; font-weight: 700; color: var(--card-text); }

  /* ── Barra de guardado ── */
  .save-bar {
    position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%) translateY(10px);
    background: rgb(0 0 0 / 0.9);
    color: #fff;
    border: 1.5px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.85);
    border-radius: 14px;
    padding: 12px 22px; display: flex; align-items: center; gap: 10px;
    font-size: 13px; font-weight: 600; opacity: 0;
    transition: all 0.3s; pointer-events: none; z-index: 100;
    box-shadow: 0 0 26px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.6);
  }
  .save-bar.visible { opacity: 1; transform: translateX(-50%) translateY(0); }

  .upload-progress {
    width: 100%; height: 3px; border-radius: 2px;
    background: rgb(var(--card-text-rgb) / 0.15);
    overflow: hidden; margin-top: 6px;
  }
  .upload-progress-fill {
    height: 100%; border-radius: 2px; transition: width 0.3s;
    background: linear-gradient(90deg, var(--brand-primary, #FFA700), var(--brand-secondary, #FF7300));
  }
`;

const AVATAR_COLORS = [
  ['#6366f1','#818cf8'], ['#0d9488','#2dd4bf'], ['#dc2626','#f87171'],
  ['#d97706','#fbbf24'], ['#2563eb','#60a5fa'], ['#db2777','#f472b6'],
  ['#059669','#34d399'], ['#7c3aed','#a78bfa'],
];

const LEARNING_STYLES = [
  { id:'auditory',   icon:'🎧', name:'Auditivo',    desc:'Aprendo escuchando'  },
  { id:'reading',    icon:'📖', name:'Lectura',     desc:'Aprendo leyendo'     },
  { id:'kinesthetic',icon:'⚡', name:'Kinestésico', desc:'Aprendo practicando' },
];

const VALID_STYLES = LEARNING_STYLES.map(s => s.id);
const resolveStyle = (style) => (VALID_STYLES.includes(style) ? style : 'auditory');

const getInitials = (name = '') =>
  name.split(' ').slice(0,2).map(w => w[0]?.toUpperCase()||'').join('') || '?';

export default function Settings() {
  const { user, api, setUser, refreshUser } = useAuth();

  // ── Avatar state ──────────────────────────────────────────────────────────
  const [preview,     setPreview]    = useState(null);
  const [previewFile, setPreviewFile]= useState(null);
  const [uploading,   setUploading]  = useState(false);
  const [uploadPct,   setUploadPct]  = useState(0);
  const [dragOver,    setDragOver]   = useState(false);
  const [imgError,    setImgError]   = useState(false);
  const [imgKey,      setImgKey]     = useState(0);

  const fileInputRef = useRef(null);

  // ── Profile state ─────────────────────────────────────────────────────────
  const [name,          setName]          = useState(user?.name || '');
  const [learningStyle, setLearningStyle] = useState(resolveStyle(user?.learning_style));
  const [saving,        setSaving]        = useState(false);
  const [saveBar,       setSaveBar]       = useState(false);

  const colorPair = AVATAR_COLORS[Math.abs((user?.id||'').charCodeAt(0)||0) % AVATAR_COLORS.length];

  // ── File helpers ──────────────────────────────────────────────────────────
  const acceptFile = useCallback((file) => {
    if (!file) return;
    if (!['image/jpeg','image/png','image/webp'].includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WEBP'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar 5 MB'); return;
    }
    setPreviewFile(file);
    setPreview(URL.createObjectURL(file));
    setImgError(false);
  }, []);

  const onFileChange = (e) => acceptFile(e.target.files[0]);
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files[0]); };
  const cancelPreview = () => {
    setPreview(null); setPreviewFile(null); setUploadPct(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Upload avatar ─────────────────────────────────────────────────────────
  const uploadAvatar = async () => {
    if (!previewFile) return;
    setUploading(true); setUploadPct(10);
    const ticker = setInterval(() => setUploadPct(p => Math.min(p + 15, 85)), 300);
    try {
      const form = new FormData();
      form.append('file', previewFile);
      await api.post('/auth/upload-avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadPct(100);
      await refreshUser();
      setImgError(false);
      setImgKey(k => k + 1);
      setPreview(null);
      setPreviewFile(null);
      toast.success('Foto de perfil actualizada ✓');
    } catch (err) {
      toast.error('Error al subir la imagen');
      console.error('Upload error:', err);
    } finally {
      clearInterval(ticker);
      setUploading(false);
      setTimeout(() => setUploadPct(0), 600);
    }
  };

  // ── Save profile ──────────────────────────────────────────────────────────
  const saveProfile = async () => {
    if (!name.trim()) { toast.error('El nombre no puede estar vacío'); return; }
    setSaving(true);
    try {
      await api.put('/auth/profile', { name: name.trim(), learning_style: learningStyle });
      if (setUser) {
        setUser(prev => ({ ...prev, name: name.trim(), learning_style: learningStyle }));
      }
      setSaveBar(true);
      setTimeout(() => setSaveBar(false), 2800);
    } catch {
      toast.error('No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const avatarSrc = preview || (imgError ? null :
    user?.avatar_url ? `${user.avatar_url.split('?')[0]}?t=${imgKey}` : null
  );
  const totalXp = user?.xp || 0;
  const level   = Math.floor(totalXp / 500) + 1;
  const lvlPct  = Math.round(((totalXp % 500) / 500) * 100);

  const accountRows = [
    { label:'Email', value: user?.email && !user.email.endsWith('@noemail.local') ? user.email : '—' },
    { label:'Rol',   value: user?.role || '—' },
    { label:'Nivel actual', value: `Nivel ${level} (${totalXp} XP)` },
    { label:'Miembro desde', value: user?.created_at
        ? new Date(user.created_at).toLocaleDateString('es-ES', { day:'numeric', month:'long', year:'numeric' })
        : '—' },
  ];

  return (
    <>
      <style>{STYLES}</style>

      <main className="settings-main">
        <h1 className="page-title">Mi Perfil</h1>
        <p className="page-subtitle">Personaliza tu cuenta y preferencias de aprendizaje</p>

        <div className="settings-grid">

          {/* ── LEFT COL ── */}
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

            {/* Avatar card */}
            <div className="glass-card">
              <div className="glass-card-header">
                <Camera size={15} />
                <span className="glass-card-title">Foto de perfil</span>
              </div>
              <div className="glass-card-body">
                <div className="avatar-center">

                  <button
                    type="button"
                    className="avatar-ring"
                    aria-label="Cambiar foto de perfil"
                    title="Cambiar foto"
                    onClick={() => !preview && fileInputRef.current?.click()}
                  >
                    <div className="avatar-ring-inner">
                      {avatarSrc ? (
                        <img
                          key={imgKey}
                          src={avatarSrc}
                          alt="Avatar"
                          className="avatar-img"
                          onError={() => setImgError(true)}
                        />
                      ) : (
                        <div className="avatar-initials"
                          style={{ background:`linear-gradient(135deg, ${colorPair[0]}, ${colorPair[1]})` }}>
                          {getInitials(user?.name)}
                        </div>
                      )}
                    </div>
                    {!preview && (
                      <div className="avatar-overlay">
                        <Camera size={20}/>
                        <span>Cambiar</span>
                      </div>
                    )}
                  </button>

                  <div style={{ textAlign:'center' }}>
                    <div className="profile-name">{user?.name}</div>
                    <div className="profile-role">
                      {user?.role === 'superadmin' ? '⚡ Super Admin' : user?.role === 'admin' ? '🛡️ Administrador' : '👤 Estudiante'}
                    </div>
                  </div>

                  <div className="stat-badges" style={{ width:'100%' }}>
                    <div className="stat-badge">
                      <div className="stat-badge-ico"><Zap size={16} /></div>
                      <div>
                        <div className="stat-badge-val">{totalXp}</div>
                        <div className="stat-badge-lbl">XP Total</div>
                      </div>
                    </div>
                    <div className="stat-badge">
                      <div className="stat-badge-ico">
                        <span style={{ fontSize:16 }}>🏅</span>
                      </div>
                      <div>
                        <div className="stat-badge-val">{level}</div>
                        <div className="stat-badge-lbl">Nivel · {lvlPct}%</div>
                      </div>
                    </div>
                  </div>

                  {!preview ? (
                    <button
                      type="button"
                      className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={onDrop}
                    >
                      <div className="drop-zone-icon">
                        <Upload size={18} />
                      </div>
                      <div className="drop-zone-text">Sube una foto</div>
                      <div className="drop-zone-sub">JPG, PNG o WEBP · Máx 5 MB</div>
                    </button>
                  ) : (
                    <div className="preview-wrap" style={{ width:'100%' }}>
                      <img src={preview} alt="Preview" className="preview-img"/>
                      {uploadPct > 0 && (
                        <div className="upload-progress">
                          <div className="upload-progress-fill" style={{ width:`${uploadPct}%` }}/>
                        </div>
                      )}
                      <div className="preview-actions">
                        <button
                          className="btn btn-indigo"
                          style={{ flex:1, justifyContent:'center' }}
                          onClick={uploadAvatar}
                          disabled={uploading}
                        >
                          {uploading ? <><div className="spinner"/> Subiendo…</> : <><Check size={14}/> Guardar foto</>}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={cancelPreview} disabled={uploading}>
                          <X size={14}/>
                        </button>
                      </div>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display:'none' }}
                    onChange={onFileChange}
                  />
                </div>
              </div>
            </div>

             {user?.features?.adaptive_learning && (
              <div className="glass-card" style={{ animationDelay:'0.07s' }}>
                <div className="glass-card-header">
                  <span style={{ fontSize:15 }}>🧠</span>
                  <span className="glass-card-title">Estilo de aprendizaje</span>
                </div>
                <div className="glass-card-body">
                  <div className="style-grid">
                    {LEARNING_STYLES.map(s => (
                      <button
                        type="button"
                        key={s.id}
                        aria-pressed={learningStyle === s.id}
                        className={`style-card ${learningStyle === s.id ? 'active' : ''}`}
                        onClick={() => setLearningStyle(s.id)}
                      >
                        <div className="style-card-icon">{s.icon}</div>
                        <div>
                          <div className="style-card-name">{s.name}</div>
                          <div className="style-card-desc">{s.desc}</div>
                        </div>
                        {learningStyle === s.id && (
                          <div className="style-check">
                            <Check size={10} style={{ color:'#fff' }}/>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT COL ── */}
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

            <div className="glass-card" style={{ animationDelay:'0.04s' }}>
              <div className="glass-card-header">
                <User size={15} />
                <span className="glass-card-title">Información personal</span>
              </div>
              <div className="glass-card-body">
                <div className="field-group">
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                    <div className="field">
                      <label className="field-label" htmlFor="f-name">Nombre completo</label>
                      <input id="f-name" className="field-input" value={name}
                        onChange={e => setName(e.target.value)} placeholder="Tu nombre"/>
                    </div>
                    <div className="field">
                      <label className="field-label" htmlFor="f-email">Email</label>
                      <input id="f-email" className="field-input" value={user?.email || ''} disabled placeholder="—"/>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                    <div className="field">
                      <label className="field-label" htmlFor="f-job">Puesto / Cargo</label>
                      <input id="f-job" className="field-input" value={user?.job_title || ''} disabled placeholder="—"/>
                    </div>
                    <div className="field">
                      <label className="field-label" htmlFor="f-dept">Departamento</label>
                      <input id="f-dept" className="field-input" value={user?.department_name || ''} disabled placeholder="—"/>
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label" htmlFor="f-org">Organización</label>
                    <input id="f-org" className="field-input" value={user?.organization?.name || '—'} disabled/>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card" style={{ animationDelay:'0.09s' }}>
              <div className="glass-card-header">
                <Shield size={15} />
                <span className="glass-card-title">Cuenta</span>
              </div>
              <div className="glass-card-body">
                <div className="account-list">
                  {accountRows.map(row => (
                    <div key={row.label} className="account-row">
                      <span className="account-row-label">{row.label}</span>
                      <span className="account-row-value">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setName(user?.name || '');
                  setLearningStyle(resolveStyle(user?.learning_style));
                }}
              >
                <RotateCcw size={14}/> Restaurar
              </button>
              <button className="btn btn-indigo" onClick={saveProfile} disabled={saving}>
                {saving ? <><div className="spinner"/> Guardando…</> : <><Check size={14}/> Guardar cambios</>}
              </button>
            </div>
          </div>
        </div>
      </main>

      <div className={`save-bar ${saveBar ? 'visible' : ''}`}>
        <Check size={15} style={{ color:'var(--brand-accent, #4ade80)' }}/>
        Cambios guardados correctamente
      </div>
    </>
  );
}